import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, ApiError } from '../api';
import { money, nfmt } from '../format';
import { lbl } from '../labels';
import { Button } from '../components/Button';
import { Card, Cards } from '../components/Card';
import { Modal } from '../components/Modal';
import { Select } from '../components/Select';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';

/**
 * Equity — the grants ledger from `/api/v1/equity` (`equityPage()`), which is what views/equity.ejs
 * renders: four totals cards, the grants table and the vesting schedule.
 *
 * The EJS page's tabs are LINKS (`/equity?tab=grants|plan`) because the page is server-rendered per
 * request; here they are button tabs, which is the same bar with the panel swapped in place. The planner
 * tab is not ported yet and says so rather than rendering an empty panel — it is a form that posts and
 * recomputes AMT scenarios, and that is the next slice.
 */
type Grant = {
  id: number;
  ticker: string;
  kind: string;
  grant_date: string;
  strike: number;
  shares: number;
  vested: number;
  available: number;
  fmv: number | null;
  fmvAsOf?: string;
  fmvStale?: boolean;
  spread: number | null;
  spreadValue?: number;
  expiry: string;
  status: string;
  isoShares?: number;
  nsoShares?: number;
};

type VestRow = { date?: string; vest_date?: string; shares: number; cumulative?: number; status?: string };

type EquityPayload = {
  year: number;
  tab: string;
  grants: Grant[];
  rows: Grant[];
  totals: { shares: number; vested: number; available: number; spreadValue: number | null };
  splitTested: boolean;
  vestGrantId: number | null;
  vestRows: VestRow[];
  today: string;
  horizon: string;
  plan: Plan | null;
  planError: string | null;
  planWarning?: string | null;
  planFmvWarning?: string | null;
  planGrantId?: number | null;
  planFmvAsOf?: string | null;
  planInput?: { grantId: number | null; shares: number; exerciseDate: string; fmv: number; otherIncome: number | null };
};

/** What `amt.plan` answers — the same object views/equity.ejs renders. */
type Plan = {
  headroom: { element: number; shares: number | null; spread: number; already?: boolean };
  baseline: { regular: number; amti: number; exemption: number; tmt: number; amt: number };
  paths: {
    id: string;
    label: string;
    character: string;
    amt: number;
    cash: number;
    taxOnGain: number;
    creditRecovered?: number | null;
    net: number;
    qualifyingDate?: string | null;
    firstYear?: { shares: number; amt: number };
    nextYear?: { shares: number; amt: number };
  }[];
  priceRows: {
    price: number;
    multiple: number;
    taxOnGain: number;
    creditRecovered?: number | null;
    net: number;
    waitNet: number;
    beats?: boolean;
  }[];
  isoShares: number;
  nsoShares: number;
  fmv: number;
  bargain: number;
  disclosure: string;
  constantsYear: number;
  best?: string;
};

export function Equity() {
  // The tab IS the URL, read reactively. It used to be a `useState` initialiser: the bar's items are LINKS (the
  // EJS bar navigates, `/equity?tab=…`), so a click changed the address and re-rendered nothing — the page sat on
  // Grants and the Exercise planner tab did nothing at all. `useSearchParams` re-renders on navigation, keeps the
  // bookmark working (`/equity?tab=plan` lands on the planner) and keeps the bar's hrefs real links.
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const tab = searchParams.get('tab') === 'plan' ? 'plan' : 'grants';
  // The bar's hrefs keep the URL's own parameters (year above all — the EJS template writes ?tab=…&year=…)
  // and change only the tab.
  const tabHref = (key: string) => {
    const qs = new URLSearchParams(search);
    qs.set('tab', key);
    // The page's own links always name the year (`/equity?tab=plan&year=2026`), and the planner reads it. A URL
    // without one would still work — the service defaults to this year — but the address would not be the one a
    // reader can bookmark or share, and the two front ends would print different bars.
    if (!qs.has('year')) qs.set('year', String(new Date().getFullYear()));
    return `/equity?${qs.toString()}`;
  };
  const [grantOpen, setGrantOpen] = useState(false);
  const [kind, setKind] = useState('iso');
  const [editing, setEditing] = useState<Grant | null>(null);
  // The tab is part of the request: the planner computes only when it is asked for ('plan'), which is the
  // same thing the EJS page's own URL does with ?tab=plan. Every query parameter the URL carries travels with it
  // (year, shares, exercise_date, grant, fmv, income): they are the planner's inputs, and dropping them made a
  // bookmarked scenario render the default one. The whole search string is in the key, so changing any of them
  // refetches — keying on the tab alone served a cached page for a different scenario.
  const apiQs = (() => {
    const qs = new URLSearchParams(search);
    qs.set('tab', tab);
    return qs.toString();
  })();
  const q = useQuery<EquityPayload, ApiError>({
    queryKey: ['equity', apiQs],
    queryFn: () => apiGet<EquityPayload>(`/api/v1/equity?${apiQs}`),
  });

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load equity: {q.error.message}</main>;

  const data = q.data!;
  const { rows, totals, splitTested, vestRows } = data;

  return (
    <main>
      <h1>Equity</h1>

      <Tabs
        id="equity-tabs"
        variant="segmented"
        raised
        ariaLabel="Equity view"
        active={tab}
        style={{ margin: '8px 0 4px' }}
        // Links, not buttons: the EJS bar is navigation (`/equity?tab=…&year=…`) and a click there is a page
        // load. The SPA reads `tab` from the URL on boot, so the same hrefs land on the same tab.
        items={[
          { key: 'grants', label: 'Grants', href: tabHref('grants') },
          { key: 'plan', label: 'Exercise planner', href: tabHref('plan') },
        ]}
      />

      {tab === 'grants' ? (
        <>
          <Cards style={{ marginTop: 16 }}>
            <Card label="Options granted" value={nfmt(Math.round(totals.shares))} />
            <Card label="Vested" value={nfmt(Math.round(totals.vested))} />
            <Card label="Exercisable today" value={nfmt(Math.round(totals.available))} />
            <Card label={lbl('Spread value')} value={totals.spreadValue == null ? '—' : money(totals.spreadValue)} />
          </Cards>

          <section className="panel-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <h2 style={{ margin: 0 }}>Your grants</h2>
              {/* The EJS control is a 30x30 icon button with the + as its glyph (aria-label "Add a grant"),
                  not a labelled 35px button: the row's height is the h2's line box, and a standard-height
                  button made this panel 5px taller than its EJS pair. */}
              <Button
                variant="quiet"
                shape="icon"
                className="t-h2"
                id="grant-add-btn"
                ariaLabel="Add a grant"
                title="Add a grant"
                onClick={() => setGrantOpen(true)}
              >
                +
              </Button>
            </div>
            {rows.length === 0 ? (
              <p className="muted t-body">
                No grants yet. Use the + button above and the planner can tell you how much you can exercise without AMT.
              </p>
            ) : (
              <Table
                expandable={false}
                tableId="grants-table"
                tableClass=""
                columns={[
                  { label: 'Ticker' },
                  { label: 'Type' },
                  { label: 'Granted' },
                  { label: 'Strike', num: true },
                  { label: 'Shares', num: true },
                  { label: 'Vested', num: true },
                  { label: 'Available', num: true },
                  { label: 'FMV', num: true },
                  { label: 'Spread', num: true },
                  { label: 'Expires' },
                  { label: 'Status' },
                  { label: '' },
                ]}
                rows={rows.map((g) => ({
                  key: g.id,
                  cells: [
                    <span key="t">
                      <strong>{g.ticker}</strong>
                      {g.kind === 'iso' && (g.nsoShares ?? 0) > 0 ? (
                        <div className="muted t-small">
                          {nfmt(Math.round(g.isoShares ?? 0))} ISO / {nfmt(Math.round(g.nsoShares ?? 0))} NSO this year
                        </div>
                      ) : null}
                    </span>,
                    g.kind.toUpperCase(),
                    g.grant_date,
                    money(g.strike),
                    nfmt(g.shares),
                    nfmt(Math.round(g.vested)),
                    nfmt(Math.round(g.available)),
                    <span key="f">
                      {g.fmv == null ? '—' : money(g.fmv)}
                      {g.fmvAsOf ? (
                        <div className={(g.fmvStale ? 'neg' : 'muted') + ' t-small'}>
                          {g.fmvAsOf}
                          {g.fmvStale ? ' · stale' : ''}
                        </div>
                      ) : null}
                    </span>,
                    <span key="s" className={(g.spreadValue ?? 0) > 0 ? 'pos' : undefined}>
                      {g.spread == null ? '—' : money(g.spread)}
                    </span>,
                    g.expiry,
                    g.status,
                    // The row's own affordance: edit is a link, because it names a grant by id the way the
                    // EJS page's does (/equity?edit=N), and the click is handled here.
                    <a
                      key="e"
                      href={`/equity?edit=${g.id}`}
                      className="t-small"
                      onClick={(e) => {
                        e.preventDefault();
                        setEditing(g);
                        setGrantOpen(true);
                      }}
                    >
                      Edit
                    </a>,
                  ],
                }))}
              />
            )}
            {!splitTested ? (
              <p className="muted t-small" style={{ marginTop: 10 }}>
                A grant has no grant-date FMV on file, so the §422(d) $100,000 ISO test cannot be run for it —
                those shares are shown as ISO.
              </p>
            ) : null}
          </section>

          <section className="panel-card">
            <h2>Vesting schedule</h2>
            {/* EJS asks the two conditions separately: no grants at all -> "Add a grant to see its vest dates.";
                grants but none selected -> "Pick a grant…". The SPA collapsed them, so an empty book showed the
                pick-a-grant line as well as the no-grants one. */}
            {rows.length === 0 ? (
              <p className="muted t-body">Add a grant to see its vest dates.</p>
            ) : vestRows.length === 0 ? (
              <p className="muted t-body">Pick a grant to see its vest dates.</p>
            ) : (
              <Table
                expandable={false}
                columns={[{ label: 'Vest date' }, { label: 'Shares', num: true }, { label: 'Cumulative', num: true }, { label: 'Status' }]}
                rows={vestRows.map((v, i) => ({
                  key: i,
                  cells: [v.vest_date ?? v.date ?? '', nfmt(v.shares), v.cumulative == null ? '' : nfmt(v.cumulative), v.status ?? ''],
                }))}
              />
            )}
          </section>
        </>
      ) : (
        <Planner data={data} />
      )}

      {/* Add a grant. The form posts the same fields the EJS modal does, through the same service
          (`/api/v1/equity/grants` -> equity.saveGrant). */}
      <Modal
        id="grant-modal"
        open={grantOpen}
        onClose={() => {
          setGrantOpen(false);
          setEditing(null);
        }}
        title={editing ? `Edit the ${editing.ticker} grant` : 'Add a grant'}
      >
        {/* A real form to the real URL: the POST redirects back to the grants tab, which is how the EJS
            modal saves a grant, and the row is there when the page lands. */}
        <form className="form-grid" method="post" action={editing ? `/equity/grants/${editing.id}` : '/equity/grants'}>
          <label className="field t-small muted">
            Ticker
            <input name="ticker" required autoComplete="off" defaultValue={editing?.ticker ?? ''} />
          </label>
          <label className="field t-small muted">
            Type
            <Select
              id="grant-kind"
              name="kind"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'iso', label: 'ISO' },
                { value: 'nso', label: 'NSO' },
              ]}
            />
            <input type="hidden" name="kind" value={kind} />
          </label>
          <label className="field t-small muted">
            Grant date
            <input type="date" name="grant_date" required defaultValue={editing?.grant_date ?? ''} />
          </label>
          <label className="field t-small muted">
            Strike
            <input type="number" name="strike" step="0.0001" min="0" required defaultValue={editing?.strike ?? ''} />
          </label>
          <label className="field t-small muted">
            Shares
            <input type="number" name="shares" step="1" min="1" required defaultValue={editing?.shares ?? ''} />
          </label>
          <label className="field t-small muted">
            FMV at grant
            <input type="number" name="fmv_at_grant" step="0.0001" min="0" />
          </label>
          <label className="field t-small muted">
            Vest start
            <input type="date" name="vest_start" />
          </label>
          <label className="field t-small muted">
            Vest interval (months)
            <input type="number" name="vest_interval_months" step="1" min="1" defaultValue={1} />
          </label>
          <label className="field t-small muted">
            Vest periods
            <input type="number" name="vest_periods" step="1" min="1" defaultValue={48} />
          </label>
          <label className="field t-small muted">
            Cliff periods
            <input type="number" name="cliff_periods" step="1" min="0" defaultValue={0} />
          </label>
          <label className="field t-small muted">
            Expiration
            <input type="date" name="expiration_date" />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', gridColumn: '1 / -1' }}>
            <Button
              id="grant-cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                setGrantOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button id="grant-modal-save" type="submit">
              Save grant
            </Button>
          </div>
        </form>
      </Modal>
    </main>
  );
}

/**
 * The exercise planner: how much of a grant can be exercised before AMT, what each exercise-and-sell path
 * costs, and what happens if the stock moves. Every figure comes from `amt.plan` in the page payload — the
 * same call the EJS page renders — so the two front ends cannot disagree about a number.
 */
function Planner({ data }: { data: EquityPayload }) {
  const plan = data.plan as Plan | null;
  if (data.planError) {
    return (
      <section className="panel-card">
        <h2>Exercise planner</h2>
        <p className="neg">{data.planError}</p>
      </section>
    );
  }
  if (!data.grants?.length || !plan) {
    return (
      <section className="panel-card">
        <h2>Exercise planner</h2>
        <p className="muted t-body">
          Add a grant on the <a href={`/equity?tab=grants&year=${data.year}`}>Grants</a> tab first — the planner
          answers how much of it you can exercise this year without AMT.
        </p>
      </section>
    );
  }
  const baseline: [string, string][] = [
    ['Regular federal income tax', money(plan.baseline.regular)],
    ['AMTI before any exercise', money(plan.baseline.amti)],
    ['AMT exemption', money(plan.baseline.exemption)],
    ['Tentative minimum tax', money(plan.baseline.tmt)],
    ['AMT before any exercise', money(plan.baseline.amt)],
  ];
  return (
    <>
      <section className="panel-card" style={{ position: 'relative' }}>
        <h2>{data.year} AMT-free room</h2>
        {plan.headroom.already ? (
          <p className="neg" style={{ margin: '6px 0 0' }}>
            This year already owes AMT before any option exercise — there is no AMT-free room left.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', margin: '6px 0 0' }}>
              <span className="t-title" style={{ fontWeight: 600 }}>
                {money(plan.headroom.element)}
              </span>
              {plan.headroom.shares != null ? (
                <span className="muted t-body">
                  ≈ <strong>{nfmt(Math.floor(plan.headroom.shares))}</strong> shares at a{' '}
                  {money(plan.headroom.spread)} spread
                </span>
              ) : null}
            </div>
            <p className="muted t-small" style={{ margin: '6px 0 0' }}>
              Bargain element you can recognize before the tentative minimum tax overtakes your regular tax.
            </p>
          </>
        )}
        {/* A label/value table with no header: the labels ARE the columns' content. */}
        <Table
          header={false}
          expandable={false}
          wrap
          tableClass=""
          style={{ marginTop: 14 }}
          columns={[{ label: '' }, { label: '', num: true }]}
          rows={baseline.map(([k, v], i) => ({
            key: i,
            cells: [
              i === baseline.length - 1 ? <strong key="k">{k}</strong> : k,
              i === baseline.length - 1 ? <strong key="v">{v}</strong> : v,
            ],
          }))}
        />
      </section>

      <section className="panel-card">
        <h2>Exercise scenarios</h2>
        <form method="get" action="/equity" className="form-grid" style={{ margin: '0 0 14px' }}>
          <input type="hidden" name="tab" value="plan" />
          <input type="hidden" name="year" value={data.year} />
          <label className="field t-small muted">
            Grant
            <select name="grant" defaultValue={String(data.planGrantId || '')}>
              {(data.grants || []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.ticker} · {String(g.kind).toUpperCase()} · {g.grant_date}
                </option>
              ))}
            </select>
          </label>
          <label className="field t-small muted">
            Shares
            <input type="number" min={0} step={1} name="shares" defaultValue={data.planInput?.shares ?? 0} />
          </label>
          <label className="field t-small muted">
            Exercise date
            <input type="date" name="exercise_date" defaultValue={data.planInput?.exerciseDate ?? data.today} />
          </label>
          <label className="field t-small muted">
            FMV
            <input type="number" min={0} step={0.0001} name="fmv" defaultValue={data.planInput?.fmv ?? 0} />
          </label>
          <label className="field t-small muted">
            Other income
            <input
              type="number"
              min={0}
              step={1}
              name="income"
              defaultValue={data.planInput?.otherIncome ?? ''}
              placeholder="from profile"
            />
          </label>
          <Button type="submit" className="primary">
            Calculate
          </Button>
        </form>

        {data.planWarning ? <p className="neg t-small" style={{ margin: '0 0 10px' }}>{data.planWarning}</p> : null}
        {data.planFmvWarning ? <p className="neg t-small" style={{ margin: '0 0 10px' }}>{data.planFmvWarning}</p> : null}
        <p className="muted t-small" style={{ margin: '0 0 10px' }}>
          {nfmt(plan.isoShares)} ISO
          {plan.nsoShares > 0 ? ` · ${nfmt(plan.nsoShares)} NSO` : ''} · FMV {money(plan.fmv)} as of{' '}
          {data.planFmvAsOf || 'unknown'} · bargain element {money(plan.bargain)}
        </p>

        <Table
          expandable={false}
          tableId="scenario-table"
          tableClass=""
          columns={[
            { label: 'Path' },
            { label: 'AMT this year', num: true },
            { label: 'Cash to exercise', num: true },
            { label: 'Tax on the gain', num: true },
            { label: 'Credit back', num: true },
            { label: 'Net after tax', num: true },
            { label: 'Earliest qualifying sale' },
          ]}
          rows={plan.paths.map((p) => ({
            key: p.id,
            rowAttrs: p.id === plan.best ? { style: 'background: rgba(80,160,255,0.06)' } : undefined,
            cells: [
              <span key="p">
                {p.label}
                <div className="muted t-small">
                  {p.character}
                  {p.id === plan.best ? ' · best after tax' : ''}
                </div>
              </span>,
              <span key="a" className={p.amt > 0 ? 'neg' : ''}>{money(p.amt)}</span>,
              money(p.cash),
              money(p.taxOnGain),
              p.creditRecovered ? money(p.creditRecovered) : '—',
              <strong key="n">{money(p.net)}</strong>,
              <span key="q">
                {p.qualifyingDate || '—'}
                {p.id === 'split' && p.firstYear && p.nextYear ? (
                  <div className="muted t-small">
                    {nfmt(Math.round(p.firstYear.shares))} now · AMT {money(p.firstYear.amt)};{' '}
                    {nfmt(Math.round(p.nextYear.shares))} in {data.year + 1} · AMT {money(p.nextYear.amt)}
                  </div>
                ) : null}
              </span>,
            ],
          }))}
        />
      </section>

      <section className="panel-card">
        <h2>If the stock moves</h2>
        <p className="muted t-small" style={{ margin: '0 0 10px' }}>
          The same exercise-and-hold path, repriced. "Beats waiting" means the after-tax cash beats exercising
          later at the price you expect.
        </p>
        <Table
          expandable={false}
          wrap
          tableClass=""
          columns={[
            { label: 'Sale price' },
            { label: 'Tax on the gain', num: true },
            { label: 'Credit back', num: true },
            { label: 'Net after tax', num: true },
            { label: 'If you had waited', num: true },
            { label: 'Beats waiting' },
          ]}
          rows={plan.priceRows.map((r, i) => ({
            key: i,
            cells: [
              <span key="p">
                {money(r.price)} <span className="muted t-small">({priceLabel(r.multiple)})</span>
              </span>,
              money(r.taxOnGain),
              r.creditRecovered ? money(r.creditRecovered) : '—',
              <span key="n" className={r.net < 0 ? 'neg' : ''}>{money(r.net)}</span>,
              money(r.waitNet),
              r.beats ? 'Yes' : 'No',
            ],
          }))}
        />
      </section>

      <section className="panel-card">
        <h2>Assumptions</h2>
        <p className="muted t-small" style={{ margin: 0 }}>
          {plan.disclosure} Constants: {plan.constantsYear} federal AMT law.
        </p>
      </section>
    </>
  );
}

/** A price row's multiple as the fraction a reader compares against: "−50%", "FMV", "+25%". */
function priceLabel(multiple: number): string {
  if (multiple === 1) return 'FMV';
  const pct = Math.round((multiple - 1) * 100);
  return pct > 0 ? `+${pct}%` : `${pct}%`;
}
